import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Crown } from "lucide-react";

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/members");
      setMembers(data);
    } catch {
      toast.error("Üyeler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const changeRole = async (uid, role) => {
    try {
      await api.put(`/team/members/${uid}/role`, { role });
      toast.success("Rol güncellendi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rol güncellenemedi");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 mb-1">
          Yönetim
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-50 flex items-center gap-3">
          <Users className="w-7 h-7 text-cyan-400" />
          Ekip Üyeleri
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Yöneticiler üye rollerini değiştirebilir. İlk giriş yapan kullanıcı
          otomatik olarak admin olur.
        </p>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#111726] overflow-hidden" data-testid="team-members-list">
        <div className="hidden sm:grid grid-cols-[1fr_1fr_auto_160px] gap-4 px-5 py-3 border-b border-slate-800 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          <div>Üye</div>
          <div>E-posta</div>
          <div>Katılım</div>
          <div>Rol</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-slate-400">Yükleniyor...</div>
        ) : (
          members.map((m) => (
            <div
              key={m.user_id}
              data-testid={`member-row-${m.user_id}`}
              className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_160px] gap-4 items-center px-5 py-4 border-b border-slate-800 last:border-b-0"
            >
              <div className="flex items-center gap-3">
                <Avatar className="w-9 h-9 border border-slate-700">
                  <AvatarImage src={m.picture} />
                  <AvatarFallback className="bg-cyan-500/20 text-cyan-300 text-xs font-mono">
                    {(m.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-sm text-slate-100 flex items-center gap-1.5">
                    {m.name}
                    {m.role === "admin" && (
                      <Crown className="w-3.5 h-3.5 text-amber-400" />
                    )}
                  </div>
                  {m.user_id === user?.user_id && (
                    <div className="text-[10px] font-mono text-emerald-400">
                      Siz
                    </div>
                  )}
                </div>
              </div>
              <div className="text-sm font-mono text-slate-400 truncate">
                {m.email}
              </div>
              <div className="text-xs font-mono text-slate-500">
                {new Date(m.created_at).toLocaleDateString("tr-TR")}
              </div>
              <div>
                {user?.role === "admin" ? (
                  <Select
                    value={m.role}
                    onValueChange={(v) => changeRole(m.user_id, v)}
                  >
                    <SelectTrigger
                      data-testid={`role-select-${m.user_id}`}
                      className="bg-[#0d121f] border-slate-800 text-slate-100 h-9"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111726] border-slate-800 text-slate-100">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Üye</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge
                    className={
                      m.role === "admin"
                        ? "bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono text-[10px] uppercase tracking-widest"
                        : "bg-slate-800 border border-slate-700 text-slate-300 font-mono text-[10px] uppercase tracking-widest"
                    }
                  >
                    {m.role}
                  </Badge>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
